import React, { useState, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Area,
  AreaChart
} from 'recharts';
import {
  Activity,
  Brain,
  Image as ImageIcon,
  Users,
  DollarSign,
  TrendingUp,
  Clock,
  Zap,
  AlertCircle,
  CheckCircle,
  Download,
  RefreshCw,
  Filter,
  Calendar,
  Search,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import toast from 'react-hot-toast';

const UsageDashboard = ({ themeClasses }) => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [dailyUsage, setDailyUsage] = useState([]);
  const [records, setRecords] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [timeframe, setTimeframe] = useState('30d');
  const [selectedService, setSelectedService] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [expandedRecord, setExpandedRecord] = useState(null);
  const [filters, setFilters] = useState({
    page: 1,
    service: '',
    provider: '',
    success: '',
    dateFrom: '',
    dateTo: '',
    userId: ''
  });

  useEffect(() => {
    loadDashboardData();
  }, [timeframe, selectedService]);

  useEffect(() => {
    loadUsageRecords();
  }, [filters]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [statsResponse, dailyResponse] = await Promise.all([
        fetch(`/api/usage/stats?timeframe=${timeframe}${selectedService !== 'all' ? `&service=${selectedService}` : ''}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }),
        fetch(`/api/usage/daily?timeframe=${timeframe}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        })
      ]);

      if (statsResponse.ok && dailyResponse.ok) {
        const statsData = await statsResponse.json();
        const dailyData = await dailyResponse.json();
        
        setStats(statsData);
        setDailyUsage(dailyData);
      } else {
        toast.error('Failed to load usage data');
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      toast.error('Failed to load usage data');
    } finally {
      setLoading(false);
    }
  };

  const loadUsageRecords = async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await fetch(`/api/usage/records?${params}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        const data = await response.json();
        setRecords(data.records);
        setPagination(data.pagination);
      }
    } catch (error) {
      console.error('Failed to load usage records:', error);
    }
  };

  const exportUsageData = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);
      if (selectedService !== 'all') params.append('service', selectedService);

      const response = await fetch(`/api/usage/export/csv?${params}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `usage-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Usage data exported successfully');
      } else {
        toast.error('Failed to export usage data');
      }
    } catch (error) {
      toast.error('Failed to export usage data');
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1
    }));
  };

  const clearFilters = () => {
    setFilters({
      page: 1,
      service: '',
      provider: '',
      success: '',
      dateFrom: '',
      dateTo: '',
      userId: ''
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    }).format(amount || 0);
  };

  const formatDuration = (duration) => {
    if (!duration) return '-';
    if (duration < 1000) return `${duration}ms`;
    return `${(duration / 1000).toFixed(2)}s`;
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const getServiceIcon = (service) => {
    return service === 'ai' ? (
      <Brain className="w-4 h-4 text-purple-500" />
    ) : (
      <ImageIcon className="w-4 h-4 text-blue-500" />
    );
  };

  const getSuccessIcon = (success) => {
    return success ? (
      <CheckCircle className="w-4 h-4 text-green-500" />
    ) : (
      <AlertCircle className="w-4 h-4 text-red-500" />
    );
  };

  // Calculate overview metrics
  const getOverviewMetrics = () => {
    if (!stats) return {};

    const services = Object.values(stats).flat();
    const totalRequests = services.reduce((sum, service) => sum + (service.count || 0), 0);
    const totalCost = services.reduce((sum, service) => sum + (service.totalCost || 0), 0);
    const avgDuration = services.reduce((sum, service) => sum + (service.avgDuration || 0), 0) / services.length;
    const avgSuccessRate = services.reduce((sum, service) => sum + (service.successRate || 0), 0) / services.length;

    return {
      totalRequests,
      totalCost,
      avgDuration: Math.round(avgDuration || 0),
      avgSuccessRate: Math.round(avgSuccessRate || 0)
    };
  };

  const overview = getOverviewMetrics();

  // Colors for charts
  const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#8dd1e1'];

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className={`ml-3 ${themeClasses.text}`}>Loading usage data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Controls */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className={`text-2xl font-bold ${themeClasses.heading}`}>Usage Analytics</h2>
          <p className={themeClasses.text}>Monitor AI and Image service usage across the platform</p>
        </div>
        <div className="flex items-center space-x-4">
          <select
            value={timeframe}
            onChange={(e) => setTimeframe(e.target.value)}
            className={`${themeClasses.input} min-w-32`}
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
          <select
            value={selectedService}
            onChange={(e) => setSelectedService(e.target.value)}
            className={`${themeClasses.input} min-w-32`}
          >
            <option value="all">All Services</option>
            <option value="ai">AI Service</option>
            <option value="image">Image Service</option>
          </select>
          <button
            onClick={loadDashboardData}
            className={`${themeClasses.buttonSecondary} flex items-center`}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className={`${themeClasses.card} p-6`}>
          <div className="flex items-center">
            <Activity className="w-8 h-8 text-blue-500 mr-3" />
            <div>
              <p className={`text-sm ${themeClasses.textMuted}`}>Total Requests</p>
              <p className={`text-2xl font-bold ${themeClasses.heading}`}>
                {overview.totalRequests?.toLocaleString() || '0'}
              </p>
            </div>
          </div>
        </div>

        <div className={`${themeClasses.card} p-6`}>
          <div className="flex items-center">
            <DollarSign className="w-8 h-8 text-green-500 mr-3" />
            <div>
              <p className={`text-sm ${themeClasses.textMuted}`}>Total Cost</p>
              <p className={`text-2xl font-bold ${themeClasses.heading}`}>
                {formatCurrency(overview.totalCost)}
              </p>
            </div>
          </div>
        </div>

        <div className={`${themeClasses.card} p-6`}>
          <div className="flex items-center">
            <Clock className="w-8 h-8 text-yellow-500 mr-3" />
            <div>
              <p className={`text-sm ${themeClasses.textMuted}`}>Avg Duration</p>
              <p className={`text-2xl font-bold ${themeClasses.heading}`}>
                {formatDuration(overview.avgDuration)}
              </p>
            </div>
          </div>
        </div>

        <div className={`${themeClasses.card} p-6`}>
          <div className="flex items-center">
            <TrendingUp className="w-8 h-8 text-purple-500 mr-3" />
            <div>
              <p className={`text-sm ${themeClasses.textMuted}`}>Success Rate</p>
              <p className={`text-2xl font-bold ${themeClasses.heading}`}>
                {overview.avgSuccessRate}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Usage Trend */}
        <div className={themeClasses.card}>
          <div className={`p-6 border-b ${themeClasses.border}`}>
            <h3 className={`text-lg font-semibold ${themeClasses.heading}`}>Daily Usage Trend</h3>
          </div>
          <div className="p-6">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={dailyUsage}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="_id.date" 
                  tickFormatter={(value) => new Date(value).toLocaleDateString()}
                />
                <YAxis />
                <Tooltip 
                  labelFormatter={(value) => new Date(value).toLocaleDateString()}
                  formatter={(value, name) => [value, name === 'ai' ? 'AI Requests' : 'Image Requests']}
                />
                <Area 
                  type="monotone" 
                  dataKey="count" 
                  stackId="1"
                  stroke="#8884d8" 
                  fill="#8884d8" 
                  fillOpacity={0.6}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Service Distribution */}
        {stats && (
          <div className={themeClasses.card}>
            <div className={`p-6 border-b ${themeClasses.border}`}>
              <h3 className={`text-lg font-semibold ${themeClasses.heading}`}>Service Usage Distribution</h3>
            </div>
            <div className="p-6">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={Object.entries(stats).map(([service, data]) => ({
                      name: service.toUpperCase(),
                      value: Array.isArray(data) ? data.reduce((sum, item) => sum + item.count, 0) : 0
                    }))}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {Object.keys(stats).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      {/* Service Breakdown */}
      {stats && (
        <div className={themeClasses.card}>
          <div className={`p-6 border-b ${themeClasses.border}`}>
            <h3 className={`text-lg font-semibold ${themeClasses.heading}`}>Service Performance Breakdown</h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {Object.entries(stats).map(([service, serviceData]) => (
                <div key={service}>
                  <div className="flex items-center mb-4">
                    {getServiceIcon(service)}
                    <h4 className={`ml-2 text-lg font-medium ${themeClasses.heading}`}>
                      {service.toUpperCase()} Service
                    </h4>
                  </div>
                  
                  {Array.isArray(serviceData) && serviceData.length > 0 ? (
                    <div className="space-y-3">
                      {serviceData.map((item, index) => (
                        <div key={index} className={`p-4 rounded-lg ${themeClasses.hover}`}>
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className={`font-medium ${themeClasses.heading}`}>
                                {item._id?.provider || 'Unknown'} - {item._id?.model || item._id?.operation || 'Default'}
                              </p>
                              <p className={`text-sm ${themeClasses.textMuted}`}>
                                {item.count} requests • {item.uniqueUserCount} users
                              </p>
                            </div>
                            <span className={`text-sm px-2 py-1 rounded ${
                              item.successRate > 0.9 ? 'bg-green-100 text-green-800' :
                              item.successRate > 0.7 ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {Math.round(item.successRate * 100)}% success
                            </span>
                          </div>
                          <div className="flex justify-between text-sm text-gray-600">
                            <span>Avg Duration: {formatDuration(item.avgDuration)}</span>
                            <span>Total Duration: {formatDuration(item.totalDuration)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className={themeClasses.textMuted}>No data available for this service</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Usage Records */}
      <div className={themeClasses.card}>
        <div className={`p-6 border-b ${themeClasses.border}`}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className={`text-lg font-semibold ${themeClasses.heading}`}>Usage Records</h3>
              <p className={themeClasses.text}>Detailed usage activity logs</p>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`${themeClasses.buttonSecondary} flex items-center`}
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
                {showFilters ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
              </button>
              <button
                onClick={exportUsageData}
                className={`${themeClasses.button} flex items-center`}
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </button>
            </div>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <div>
                  <select
                    value={filters.service}
                    onChange={(e) => handleFilterChange('service', e.target.value)}
                    className={`w-full ${themeClasses.input}`}
                  >
                    <option value="">All Services</option>
                    <option value="ai">AI Service</option>
                    <option value="image">Image Service</option>
                  </select>
                </div>

                <div>
                  <select
                    value={filters.provider}
                    onChange={(e) => handleFilterChange('provider', e.target.value)}
                    className={`w-full ${themeClasses.input}`}
                  >
                    <option value="">All Providers</option>
                    <option value="ollama">Ollama</option>
                    <option value="groq">Groq</option>
                    <option value="unsplash">Unsplash</option>
                    <option value="pexels">Pexels</option>
                  </select>
                </div>

                <div>
                  <select
                    value={filters.success}
                    onChange={(e) => handleFilterChange('success', e.target.value)}
                    className={`w-full ${themeClasses.input}`}
                  >
                    <option value="">All Results</option>
                    <option value="true">Success Only</option>
                    <option value="false">Errors Only</option>
                  </select>
                </div>

                <div>
                  <input
                    type="text"
                    placeholder="User ID"
                    value={filters.userId}
                    onChange={(e) => handleFilterChange('userId', e.target.value)}
                    className={`w-full ${themeClasses.input}`}
                  />
                </div>

                <div>
                  <input
                    type="datetime-local"
                    value={filters.dateFrom}
                    onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                    className={`w-full ${themeClasses.input}`}
                  />
                </div>

                <div>
                  <input
                    type="datetime-local"
                    value={filters.dateTo}
                    onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                    className={`w-full ${themeClasses.input}`}
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={clearFilters}
                  className={themeClasses.buttonSecondary}
                >
                  Clear All Filters
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Records List */}
        <div className="divide-y divide-gray-200">
          {records.map((record) => (
            <div key={record._id} className={`p-6 ${themeClasses.hover}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4 flex-1">
                  <div className="flex-shrink-0">
                    {getServiceIcon(record.service)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                        record.service === 'ai' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {record.service.toUpperCase()}
                      </span>
                      <span className={`text-sm font-medium ${themeClasses.heading}`}>
                        {record.serviceDetails?.provider || 'Unknown'} - {record.serviceDetails?.operation || 'Operation'}
                      </span>
                      {getSuccessIcon(record.serviceDetails?.success)}
                      <span className={`text-sm ${themeClasses.textMuted}`}>
                        {formatTimestamp(record.timestamp)}
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <span>User: {record.userId?.name || 'Unknown'}</span>
                      {record.context?.siteName && (
                        <span>Site: {record.context.siteName}</span>
                      )}
                      {record.context?.sectionType && (
                        <span>Section: {record.context.sectionType}</span>
                      )}
                      <span>Duration: {formatDuration(record.serviceDetails?.duration)}</span>
                      <span>Cost: {formatCurrency(record.billing?.cost)}</span>
                    </div>
                    
                    {record.serviceDetails?.error && (
                      <p className="text-sm text-red-600 mt-2">
                        Error: {record.serviceDetails.error}
                      </p>
                    )}
                  </div>
                </div>
                
                <button
                  onClick={() => setExpandedRecord(expandedRecord === record._id ? null : record._id)}
                  className={themeClasses.iconButton}
                >
                  {expandedRecord === record._id ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              
              {/* Expanded Details */}
              {expandedRecord === record._id && (
                <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <pre className="text-xs text-gray-600 dark:text-gray-400 overflow-x-auto whitespace-pre-wrap">
                    {JSON.stringify(record, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className={`px-6 py-4 border-t ${themeClasses.border} flex items-center justify-between`}>
            <div className={`text-sm ${themeClasses.textMuted}`}>
              Showing {((pagination.currentPage - 1) * 50) + 1} to {Math.min(pagination.currentPage * 50, pagination.totalRecords)} of {pagination.totalRecords} records
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handleFilterChange('page', Math.max(1, filters.page - 1))}
                disabled={!pagination.hasPrev}
                className={`px-3 py-1 text-sm border rounded ${!pagination.hasPrev ? 'opacity-50 cursor-not-allowed' : themeClasses.buttonSecondary}`}
              >
                Previous
              </button>
              <span className={`text-sm ${themeClasses.text}`}>
                Page {pagination.currentPage} of {pagination.totalPages}
              </span>
              <button
                onClick={() => handleFilterChange('page', Math.min(pagination.totalPages, filters.page + 1))}
                disabled={!pagination.hasNext}
                className={`px-3 py-1 text-sm border rounded ${!pagination.hasNext ? 'opacity-50 cursor-not-allowed' : themeClasses.buttonSecondary}`}
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && records.length === 0 && (
          <div className="text-center py-12">
            <Activity className={`w-12 h-12 ${themeClasses.textMuted} mx-auto mb-4`} />
            <p className={themeClasses.textMuted}>
              {Object.values(filters).some(v => v && v !== 1) ? 'No usage records match your filters' : 'No usage records found'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsageDashboard;